"use client";

import Link from "next/link";
import Image from "next/image";
import {
  Phone,
  FileText,
  Shield,
  Clock,
  Award,
  Flame,
  BadgeCheck,
  DollarSign,
  ThumbsUp,
  Star,
  MapPin,
  ArrowRight,
  Wrench,
  HardHat,
  Search,
  ShieldCheck,
  Hammer,
  Wind,
  Droplets,
  ClipboardCheck,
  Building2,
  Layers,
  CheckCircle,
  Users,
  Truck,
  PhoneCall,
  Calendar,
  CircleDollarSign,
} from "lucide-react";
import { SITE, SERVICES, LOCATIONS } from "@/lib/siteData";

const SERVICE_ICONS: Record<string, React.ReactNode> = {
  Wrench: <Wrench className="h-8 w-8" />,
  Search: <Search className="h-8 w-8" />,
  ShieldCheck: <ShieldCheck className="h-8 w-8" />,
  Hammer: <Hammer className="h-8 w-8" />,
  Droplets: <Droplets className="h-8 w-8" />,
  Layers: <Layers className="h-8 w-8" />,
  HardHat: <HardHat className="h-8 w-8" />,
  Wind: <Wind className="h-8 w-8" />,
  Flame: <Flame className="h-8 w-8" />,
  ClipboardCheck: <ClipboardCheck className="h-8 w-8" />,
  Building2: <Building2 className="h-8 w-8" />,
};

const topLocations = LOCATIONS.filter((l) => l.isTop);

/* ---------- Hero Section ---------- */
export function HeroSection() {
  return (
    <section className="relative bg-[#FFFFFF] overflow-hidden">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#B98002]/30 via-transparent to-[#E8BD4E]/10" />

      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:py-28 lg:py-36">
        <div className="flex items-center gap-12">
        <div className="max-w-3xl flex-1">
          {/* Available Now badge */}
          <div className="inline-flex items-center gap-2 bg-[#1D7A2E]/15 text-[#1D7A2E] text-sm font-medium px-4 py-2 rounded-full mb-6 border border-[#1D7A2E]/20">
            <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#1D7A2E] opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#1D7A2E]" />
            </span>
            Booking Now — Same-Day Sweeps Available
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#0A0A0A] leading-tight tracking-tight">
            Premium{" "}
            <span className="text-[#7A5500]">Chimney Sweep, Inspection &amp; Repair</span>{" "}
            Across Maryland
          </h1>

          {/* Quick Answer block — GEO / AI Overview eligibility */}
          <div className="mt-6 border-l-4 border-[#E8BD4E] bg-[#F5F1E8] rounded-r-xl px-5 py-4 max-w-2xl">
            <p className="text-sm font-bold text-[#7A5500] uppercase tracking-wider mb-1">Quick Answer</p>
            <p className="text-slate-700 leading-relaxed text-sm sm:text-base">
              Eagle Chimney Service is an experienced, licensed and insured chimney company in Beltsville, MD providing NFPA 211 Level 1 &amp; 2 inspections, full sweeps, crown and cap repair, relining, waterproofing, and fireplace installation across {LOCATIONS.length} Maryland and DC communities, including Prince George&rsquo;s, Montgomery, Howard, and Anne Arundel Counties.
            </p>
          </div>

          <p className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl">
            <strong className="text-slate-700">Eagle Chimney Service has been protecting Maryland homes from chimney fires, water damage, and carbon-monoxide leaks since {SITE.founded}.</strong>{" "}
            Experienced sweeps, video inspections, photo reports, and 24/7 emergency response across Beltsville, Bethesda, Silver Spring, Columbia, Baltimore, and the rest of our {LOCATIONS.length} Maryland and DC service areas.
          </p>

          {/* CTA Buttons — Premium with gradient + pulse */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href={SITE.phoneHref}
              className="inline-flex items-center justify-center gap-2 cta-gold cta-pulse text-[#0A0A0A] font-bold px-8 py-4 rounded-full transition-all min-h-[56px] text-lg shadow-lg shadow-[#E8BD4E]/30 hover:shadow-xl hover:shadow-[#E8BD4E]/40 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Phone className="h-5 w-5" />
              Call Now — {SITE.phone}
            </a>
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#B98002] hover:border-[#7A5500] active:border-[#7A5500] text-[#0A0A0A] font-bold px-8 py-4 rounded-full transition-all min-h-[56px] text-lg hover:bg-[#F5F1E8] active:bg-[#EEE9DD]"
            >
              <FileText className="h-5 w-5" />
              Get Free Quote
            </Link>
          </div>

          {/* Trust Badges */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: <Shield className="h-5 w-5" />, label: "Licensed & Insured" },
              { icon: <Award className="h-5 w-5" />, label: `Serving MD Since ${SITE.founded}` },
              { icon: <Flame className="h-5 w-5" />, label: "Owner-Operated" },
              { icon: <Clock className="h-5 w-5" />, label: "24/7 Emergency" },
            ].map((badge) => (
              <div
                key={badge.label}
                className="flex items-center gap-2.5 bg-[#F5F1E8] rounded-xl px-4 py-3 border border-stone-200"
              >
                <span className="text-[#7A5500] shrink-0">{badge.icon}</span>
                <span className="text-xs sm:text-sm font-medium text-slate-700">
                  {badge.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Hero Image - Desktop. 1024px variant (class-B generated, see IMAGE-PROVENANCE.md). */}
        <div className="hidden lg:block flex-shrink-0">
          <Image
            src="/images/hero-chimney-sweep-md-v2-1024.webp"
            alt="Chimney sweep with rods and HEPA vacuum on a brick colonial in Maryland — Eagle Chimney Service"
            width={1024}
            height={576}
            priority
            fetchPriority="high"
            quality={80}
            sizes="(max-width: 1024px) 0vw, 40vw"
            className="rounded-2xl shadow-2xl shadow-black/30 object-cover object-[center_35%] w-[440px] h-[330px] ring-1 ring-[#E8BD4E]/30"
          />
        </div>
        </div>

        {/* Hero Image - Mobile. 768px variant (class-B generated, see IMAGE-PROVENANCE.md). */}
        <div className="lg:hidden mt-8">
          <Image
            src="/images/hero-chimney-sweep-md-v2-768.webp"
            alt="Chimney sweep on a brick colonial in Maryland — Eagle Chimney Service"
            width={768}
            height={432}
            priority
            fetchPriority="high"
            quality={80}
            sizes="100vw"
            className="rounded-2xl shadow-xl shadow-black/30 object-cover object-[center_35%] w-full h-60 sm:h-72 ring-1 ring-[#E8BD4E]/30"
          />
        </div>
      </div>
    </section>
  );
}

/* ---------- Services Overview ---------- */
export function ServicesSection() {
  return (
    <section className="bg-white py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tight">
            What Chimney Services Does Eagle Offer in Maryland?
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            From annual sweeps and inspections to full relining, masonry rebuilds, and fireplace installs — every service is performed by experienced technicians to NFPA 211 code.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {SERVICES.map((service) => (
            <Link
              key={service.slug}
              href={`/services/${service.slug}`}
              className="group flex flex-col p-6 rounded-2xl border border-slate-200 hover:border-[#E8BD4E]/30 hover:shadow-lg hover:shadow-[#E8BD4E]/5 transition-all"
            >
              <div className="w-14 h-14 rounded-xl bg-[#E8BD4E]/10 text-[#7A5500] flex items-center justify-center mb-4 group-hover:bg-[#E8BD4E] group-hover:text-[#0A0A0A] transition-colors">
                {SERVICE_ICONS[service.icon] ?? <Flame className="h-8 w-8" />}
              </div>
              <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">
                {service.shortName}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed flex-1">
                {service.description}
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#7A5500] group-hover:gap-2 transition-all">
                Learn More <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Why Choose Us ---------- */
export function WhyChooseUsSection() {
  const pillars = [
    {
      icon: <Flame className="h-7 w-7" />,
      title: "Owner-Operated",
      description:
        "Every technician trained to industry standards and NFPA 211 code.",
    },
    {
      icon: <BadgeCheck className="h-7 w-7" />,
      title: "Licensed & Insured",
      description:
        "Fully licensed, bonded, and insured. Background-checked sweeps you can welcome into your home.",
    },
    {
      icon: <DollarSign className="h-7 w-7" />,
      title: "Upfront Pricing",
      description:
        "Written quote before any work begins. Photo report included with every service.",
    },
    {
      icon: <ThumbsUp className="h-7 w-7" />,
      title: "100% Satisfaction",
      description:
        "90-day labor warranty on repair work, plus manufacturer warranties — like ChimneySaver's 10-year waterproofing warranty — registered in your name.",
    },
  ];

  const stats: { value: string; suffix: string; label: string; href?: string }[] = [
    { value: "★", suffix: "", label: "Read our Google reviews", href: SITE.googleMapsReviews },
    { value: `${SITE.founded}`, suffix: "", label: "Serving Maryland Since" },
    { value: `${LOCATIONS.length}`, suffix: "", label: "Service Areas" },
    { value: "24/7", suffix: "", label: "Emergency" },
  ];

  return (
    <section className="bg-[#FFFFFF] py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tight">
            Why Choose Eagle Chimney Service in Maryland?
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Maryland trusts us for code-compliant, photo-documented{" "}
            <Link href="/services" className="text-[#7A5500] underline">chimney services</Link>.
            See our <Link href="/pricing" className="text-[#7A5500] underline">chimney cost guide</Link> or{" "}
            <Link href="/about" className="text-[#7A5500] underline">learn about our story</Link>.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {pillars.map((pillar) => (
            <div
              key={pillar.title}
              className="flex flex-col items-center text-center p-6 rounded-2xl bg-[#F5F1E8] border border-stone-200"
            >
              <div className="w-14 h-14 rounded-xl bg-[#E8BD4E]/15 text-[#7A5500] flex items-center justify-center mb-4">
                {pillar.icon}
              </div>
              <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">
                {pillar.title}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {pillar.description}
              </p>
            </div>
          ))}
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 bg-[#B98002]/30 rounded-2xl p-6 lg:p-8 border border-[#B98002]/40">
          {stats.map((stat) => {
            const body = (
              <>
                <div className="text-3xl sm:text-4xl font-black text-[#7A5500]">
                  {stat.value}
                  <span className="text-[#7A5500]">{stat.suffix}</span>
                </div>
                <div className={`text-sm mt-1 ${stat.href ? "text-[#7A5500] underline" : "text-slate-600"}`}>{stat.label}</div>
              </>
            );
            return stat.href ? (
              <a key={stat.label} href={stat.href} target="_blank" rel="noopener noreferrer" className="text-center block">
                {body}
              </a>
            ) : (
              <div key={stat.label} className="text-center">
                {body}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- Service Areas ---------- */
export function ServiceAreasSection() {
  return (
    <section className="bg-slate-50 py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tight">
            Which Maryland Cities Does Eagle Chimney Service Cover?
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Service across {LOCATIONS.length} Maryland and DC communities for{" "}
            <Link href="/services/chimney-cleaning" className="text-[#7A5500] underline font-semibold">annual sweeps</Link>,{" "}
            <Link href="/services/chimney-inspection" className="text-[#7A5500] underline font-semibold">inspections</Link>, and{" "}
            <Link href="/services/chimney-masonry-repair" className="text-[#7A5500] underline font-semibold">masonry repair</Link>. Find your area below.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {topLocations.map((loc) => (
            <Link
              key={loc.slug}
              href={`/locations/${loc.slug}`}
              className="group flex items-center justify-between p-4 rounded-xl bg-white border border-slate-200 hover:border-[#E8BD4E]/30 hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-[#7A5500] shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-[#0A0A0A] group-hover:text-[#7A5500] transition-colors">
                    {loc.city}
                  </div>
                  <div className="text-xs text-slate-600">{loc.state}</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-[#7A5500] shrink-0" aria-hidden="true" />
            </Link>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            href="/locations"
            className="inline-flex items-center gap-2 bg-[#7A5500] hover:bg-[#5A3F00] active:bg-[#5A3F00] text-white font-bold px-8 py-4 rounded-full transition-all min-h-[48px] hover:shadow-lg active:scale-[0.98]"
          >
            View All {LOCATIONS.length} Locations
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- About / Local Presence ---------- */
export function AboutSection() {
  return (
    <section className="bg-white py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block mb-3 rounded-full bg-[#E8BD4E]/10 border border-[#E8BD4E]/30 px-4 py-1.5 text-sm font-semibold text-[#7A5500]">
              About Our Company
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tight leading-tight">
              Who Is Eagle Chimney Service in Beltsville, Maryland?
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Eagle Chimney Service is a <strong>family-operated chimney company</strong> headquartered at {SITE.addressDisplay}. Since {SITE.founded}, owner Yaniv Asayag has personally trained every sweep on the team to industry standards and NFPA 211 code.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Unlike out-of-state contractors who treat chimneys like an upsell to roofing or insulation work, we are a dedicated chimney specialist. Our trucks carry HEPA-rated dual-stage vacuums, Cobra-class flexible rods, ChimneySaver vapor-permeable sealants, HeatShield ceramic relining, and stainless-steel caps in every flue size we encounter across Maryland.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Every job is photo-documented and emailed to you the same day. We do not upsell. If your flue is clean and your crown is sound, we tell you. Maryland homeowners have trusted us since {SITE.founded} because we approach every chimney as a fire-safety system first and a service ticket second.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { icon: <Shield className="h-5 w-5" />, text: "Licensed & Insured" },
                { icon: <Flame className="h-5 w-5" />, text: "Owner-Operated" },
                { icon: <Award className="h-5 w-5" />, text: `Serving MD Since ${SITE.founded}` },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2.5">
                  <span className="text-[#7A5500]">{item.icon}</span>
                  <span className="text-sm font-medium text-[#0A0A0A]">{item.text}</span>
                </div>
              ))}
              <a
                href={SITE.googleMapsReviews}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5"
              >
                <span className="text-[#7A5500]"><Star className="h-5 w-5" /></span>
                <span className="text-sm font-medium text-[#7A5500] underline">Read our Google reviews</span>
              </a>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href={SITE.phoneHref}
                className="inline-flex items-center justify-center gap-2 cta-gold text-[#0A0A0A] font-bold px-8 py-4 rounded-full transition-all min-h-[48px] shadow-lg shadow-[#E8BD4E]/30 hover:shadow-xl hover:scale-[1.02]"
              >
                <Phone className="h-5 w-5" />
                Call {SITE.phone}
              </a>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center gap-2 border-2 border-slate-300 hover:border-[#E8BD4E] text-[#0A0A0A] font-bold px-8 py-4 rounded-full transition-all min-h-[48px]"
              >
                <FileText className="h-5 w-5" />
                Get a Free Estimate
              </Link>
            </div>
          </div>

          <div className="relative space-y-4">
            {/* Featured Install Photo */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl shadow-slate-900/10">
              <Image
                src="/images/services/gas-fireplace-insert-install-living-room-md-v2.jpg"
                alt="Gas fireplace insert installation in a Maryland living room — Eagle Chimney Service"
                width={720}
                height={405}
                loading="lazy"
                quality={80}
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="w-full h-72 lg:h-80 object-cover object-[center_55%]"
              />
            </div>
            {/* Trust & Stats Panel */}
            <div className="bg-slate-50 rounded-2xl p-8 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: <Truck className="h-7 w-7" aria-hidden="true" />, value: `${LOCATIONS.length}`, label: "Service Areas" },
                  { icon: <Clock className="h-7 w-7" aria-hidden="true" />, value: "24/7", label: "Emergency" },
                  { icon: <Award className="h-7 w-7" aria-hidden="true" />, value: `${SITE.founded}`, label: "Serving MD Since" },
                  { icon: <Users className="h-7 w-7" aria-hidden="true" />, value: "Free", label: "Written Estimates" },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="w-14 h-14 mx-auto rounded-xl bg-[#E8BD4E]/10 text-[#7A5500] flex items-center justify-center mb-2">
                      {stat.icon}
                    </div>
                    <div className="text-2xl font-black text-[#0A0A0A]">{stat.value}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <a
              href={SITE.googleMapsReviews}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#FFFFFF] rounded-2xl p-5 shadow-xl flex items-center gap-4"
            >
              <Star className="h-8 w-8 fill-[#E8BD4E] text-[#7A5500] shrink-0" aria-hidden="true" />
              <div>
                <div className="text-lg font-black text-[#7A5500] underline">Read our Google reviews</div>
                <div className="text-xs text-slate-600 mt-0.5">See what Maryland homeowners say about us</div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- How It Works / Process ---------- */
export function ProcessSection() {
  const steps = [
    {
      icon: <PhoneCall className="h-7 w-7" />,
      step: "1",
      title: "Call or Request a Quote",
      description: "Call " + SITE.phone + " or fill out the form. A real person answers — no call centers, no bots.",
    },
    {
      icon: <Calendar className="h-7 w-7" />,
      step: "2",
      title: "Same-Day Scheduling",
      description: "We dispatch an experienced sweep to your home, and our line is open 24/7 for emergencies.",
    },
    {
      icon: <CircleDollarSign className="h-7 w-7" />,
      step: "3",
      title: "Inspection & Upfront Quote",
      description: "Video flue inspection, written report, and upfront pricing before any work starts. No surprise fees.",
    },
    {
      icon: <CheckCircle className="h-7 w-7" />,
      step: "4",
      title: "Job Done Clean",
      description: "Drop cloths, HEPA vacuum, photo-documented results emailed the same day. Repairs carry our 90-day labor warranty.",
    },
  ];

  return (
    <section className="bg-slate-50 py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tight">
            How Does Eagle Chimney Service Work? 4 Simple Steps
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            From your first call to a code-compliant, photo-documented chimney — here is what to expect when you choose Eagle Chimney Service.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s) => (
            <div key={s.step} className="relative bg-white rounded-2xl border border-slate-200 p-6 text-center">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-[#E8BD4E] text-[#0A0A0A] text-sm font-black flex items-center justify-center">
                {s.step}
              </div>
              <div className="w-14 h-14 mx-auto rounded-xl bg-[#E8BD4E]/10 text-[#7A5500] flex items-center justify-center mt-4 mb-4">
                {s.icon}
              </div>
              <h3 className="text-base font-bold text-[#0A0A0A] mb-2">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Brands We Service ---------- */
export function BrandsSection() {
  const brands = [
    "Vermont Castings", "Lopi", "Jotul", "Quadra-Fire", "Hearthstone",
    "Regency", "Napoleon", "ChimneySaver", "HeatShield", "Supaflu",
    "DuraVent", "ICC Excel", "Lock-Top", "Lyemance",
  ];

  return (
    <section className="bg-white py-12 lg:py-16 border-t border-slate-100">
      <div className="mx-auto max-w-7xl px-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-black text-[#0A0A0A] tracking-tight">
            Which Chimney &amp; Fireplace Brands Does Eagle Service?
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            Our experienced sweeps install, repair, and maintain inserts, stoves, liners, caps, and dampers from every major manufacturer.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
          {brands.map((brand) => (
            <span
              key={brand}
              className="text-sm sm:text-base font-semibold text-slate-600 hover:text-[#7A5500] transition-colors whitespace-nowrap"
            >
              {brand}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Local SEO Content Block ---------- */
export function LocalSeoSection() {
  return (
    <section className="bg-slate-50 py-16 lg:py-24">
      <div className="mx-auto max-w-5xl px-4">
        <h2 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tight text-center">
          Does Eagle Chimney Service Cover All of Maryland?
        </h2>

        <div className="mt-10 space-y-8 text-slate-600 leading-relaxed">
          <div>
            <h3 className="text-xl font-bold text-[#0A0A0A] mb-3">
              Professional Chimney Services in Beltsville, Silver Spring, Columbia &amp; Beyond
            </h3>
            <p>
              Whether you live in Prince George&rsquo;s County, Montgomery County, Howard County, or Anne Arundel County, Eagle Chimney Service provides fast, code-compliant <Link href="/services/chimney-cleaning" className="text-[#7A5500] font-semibold underline">chimney sweeps</Link>, <Link href="/services/chimney-inspection" className="text-[#7A5500] font-semibold underline">NFPA 211 inspections</Link>, and <Link href="/services/chimney-masonry-repair" className="text-[#7A5500] font-semibold underline">masonry repair</Link> throughout Maryland and the Washington, D.C. metro area. Our headquarters in Beltsville, MD allows us to dispatch experienced sweeps quickly to <Link href="/locations" className="text-[#7A5500] font-semibold underline">communities across central Maryland</Link>.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-[#0A0A0A] mb-3">
              Common Chimney Problems in Maryland
            </h3>
            <p>
              Maryland&rsquo;s climate punishes chimneys. Cold winters drive heavy creosote buildup — that is why annual <Link href="/services/chimney-cleaning" className="text-[#7A5500] font-semibold underline">sweeps</Link> are the most-requested service. Freeze-thaw cycles crack mortar joints and crowns. Summer humidity and heavy MD rain force water past failed <Link href="/services/chimney-flashing-repair" className="text-[#7A5500] font-semibold underline">flashing</Link> and unsealed brick. We see and fix it all:
            </p>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                "1/8\" creosote buildup (Stage 1-3)",
                "Cracked crown & spalling brick",
                "Failed flashing & ceiling stains",
                "Animal nests blocking the flue",
                "Smoke-back from negative pressure",
                "Damper stuck open (heat loss)",
                "Oversized flue + new gas insert",
                "Carbon monoxide leak risk",
                "Missing chimney cap & spark arrestor",
                "White efflorescence on brick (moisture)",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4 text-[#1D7A2E] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-bold text-[#0A0A0A] mb-3">
              Residential &amp; Commercial Chimney Experts
            </h3>
            <p>
              From historic brick chimneys in Hyattsville and Annapolis to modern pre-fab metal flues in new Columbia construction, we service every chimney type. Our residential work covers wood-burning fireplaces, gas inserts, pellet stoves, and oil-furnace flues. For businesses, we handle <Link href="/services/commercial-chimney-services" className="text-[#7A5500] font-semibold underline">commercial chimney services</Link> including boiler stacks, restaurant kitchen exhaust (Type I hoods, NFPA 96), apartment-building flues, and dryer-vent systems. No chimney is too tall, too old, or too complicated.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-[#0A0A0A] mb-3">
              24/7 Chimney Emergencies in Maryland
            </h3>
            <p>
              A chimney fire, a smoke-back into the living room, or a sudden ceiling leak does not wait for business hours. Eagle Chimney Service offers 24/7 emergency response across Maryland for active flue fires, post-fire NFPA 211 Level 2 inspections, water-leak diagnostics, and emergency cap installations after storms. Call <a href={SITE.phoneHref} className="text-[#7A5500] font-semibold underline">{SITE.phone}</a> any time — an experienced sweep is dispatched immediately.
            </p>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 cta-gold text-[#0A0A0A] font-bold px-8 py-4 rounded-full transition-all min-h-[48px] shadow-lg shadow-[#E8BD4E]/30 hover:shadow-xl hover:scale-[1.02]"
          >
            <Phone className="h-5 w-5" />
            Get a Free Estimate Today
          </Link>
        </div>
      </div>
    </section>
  );
}
